// src/pages/personal/analytics/MarketingExpensesSection/MarketingExpensesSection.tsx
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Grid, Flex } from '@/components/ui/Layout';
import { TEXT_STYLES } from '@/theme';
import { PeriodSelector } from '@/components/ui/PeriodSelector';
import { Period, DateRange } from '../types/types';
import { useResponsive } from '@/hooks/useResponsive';
import { useMarketingExpensesData } from './hooks/useMarketingExpensesData';
import MarketingMetrics from './components/MarketingMetrics';
import ExpensesChart from './components/ExpensesChart';
import SourceDistribution from './components/SourceDistribution';
import ExpensesTable from './components/ExpensesTable';
import IntegrationSettings from './components/IntegrationSettings';
import styles from './MarketingExpensesSection.module.css';

// Типы метрик для маркетинговых расходов
export type MarketingMetricType = 'totalSpend' | 'impressions' | 'clicks' | 'uniqueReach' | 'ctr';

const MarketingExpensesSection: React.FC = () => {
  const { t } = useTranslation(['expenses', 'common']);
  const { isMobile } = useResponsive();
  
  // Состояния для фильтрации и отображения
  const [period, setPeriod] = useState<Period>('month');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [selectedMetric, setSelectedMetric] = useState<MarketingMetricType>('totalSpend');
  
  // Загрузка данных из хука
  const {
    metrics,
    expensesData,
    channelDistribution,
    platformDistribution,
    isLoading,
    error  } = useMarketingExpensesData(period, dateRange);

  // Обработчик изменения периода
  const handlePeriodChange = useCallback((newPeriod: Period, newDateRange?: DateRange) => {
    setPeriod(newPeriod);
    if (newDateRange) {
      setDateRange(newDateRange);
    }
  }, []);

  // Обработчик изменения метрики
  const handleMetricChange = useCallback((metric: MarketingMetricType) => {
    setSelectedMetric(metric);
  }, []);

  return (
    <Container size="full" padding="md">
      <div className="w-full max-w-7xl mx-auto">
        <h1 className={`${TEXT_STYLES.h1} text-center mb-6`}>
          {t('marketing.page_title')}
        </h1>
        
        <Grid cols={1} gap="lg">
          {/* Метрики маркетинга */}
          <MarketingMetrics 
            metrics={metrics}
            selectedMetric={selectedMetric}
            onSelectMetric={handleMetricChange}
          />
          
          {/* Переключатель периодов и кнопки действий */}
          <Flex 
            justify="between" 
            align="center" 
            className="my-4"
            mobileDirection="column"
            mobileAlign="stretch"
            mobileGap="md"
          >
            <PeriodSelector 
              period={period} 
              onChange={handlePeriodChange} 
              showTitle={false}
              defaultPeriodType="byMonth"
            />
            
            <Flex 
              gap="sm" 
              className={isMobile ? "w-full" : ""}
              mobileDirection="column"
            >
              <IntegrationSettings />
            </Flex>
          </Flex>
          
          {/* График расходов */}
          <ExpensesChart 
            data={expensesData}
            metric={selectedMetric}
            period={period}
            isLoading={isLoading}
          />
          
          {/* Сообщение об ошибке */}
          {error && (
            <div className={styles.errorMessage}>
              {t('errors.loading_failed')}
              {process.env.NODE_ENV === 'development' && (
                <div className="mt-2 text-sm">{error}</div>
              )}
            </div>
          )}
          
          {/* Распределение расходов по каналам и платформам */}
          <div className={styles.chartSection}>
            <Grid 
              cols={1} 
              tabletCols={2}
              gap={isMobile ? "md" : "lg"}
            >
              <SourceDistribution 
                title={t('marketing.expenses.channel_distribution')}
                data={channelDistribution || []} 
                isLoading={isLoading}
                type="channel"
              />
              
              <SourceDistribution 
                title={t('marketing.expenses.platform_distribution')}
                data={platformDistribution || []} 
                isLoading={isLoading}
                type="platform"
              />
            </Grid>
          </div>
          
          {/* Таблица расходов */}
          <ExpensesTable />
        </Grid>
      </div>
    </Container>
  );
};

export default MarketingExpensesSection;